// src/components/ActivityOutcomeRatings.jsx
//
// Self-contained outcome ratings chart for a single activity + outcome.
// Fetches its own data from GET /project/:id/outcome-ratings?outcomeId=
//
// This is a specialisation of the Report Builder OutcomeRatings chart,
// sharing the same repository query underneath but accessible to all
// authenticated users (not admin-only).
//
// Props:
//   activityId  — number  (required)
//   outcomeId   — number  (required)

/* eslint-disable react-hooks/exhaustive-deps -- the fetch effect's dependency
   array is intentionally narrower than its body reads (see the comment above
   that array): it depends on a joined string of evaluationPlanIds, not the
   array itself, to avoid refetching on every parent re-render. */

import React, {
  Fragment,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useId,
  useImperativeHandle,
} from 'react';

import { useTranslation, Trans } from 'react-i18next';

import { createPortal } from 'react-dom';
import { Icon, Popup } from 'semantic-ui-react';
import axios from 'axios';

import { displayOutcome } from '../../lib/outcomeDisplayLabels';
import { LocaleService } from '../../services';

// Display-only capitalisation of method labels ("Questionnaire - proxy" -> "Questionnaire - Proxy")
function titleCaseMethod(label) {
  return String(label).replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Colour constants ──────────────────────────────────────────────────────────
const BASE_COLOR = '222,72,12'; // warm red-orange — post
const PRE_COLOR = '47,128,237'; // #2F80ED — blue — pre
const AXIS_COLOR = '#9ca3af';
const LABEL_COLOR = '#374151';

// ── Chart layout constants ────────────────────────────────────────────────────
// Widest the chart is *laid out* at. Wider than this, the finished layout is
// scaled up uniformly via the SVG viewBox (text, strokes and padding grow in
// proportion) rather than re-laid-out, so a big monitor gets a bigger chart
// that still reads the same. Also the fixed layout every PNG export uses.
const LAYOUT_MAX_PX = 1232;
const HEIGHT_CAP_MIN_PX = 800;
const PAD_LEFT = 66;
const Y_COL_X = Math.round(PAD_LEFT / 3);
const PAD_RIGHT = 150;
const PAD_TOP = 20;
const PAD_BOTTOM = 74;
const PHI = (1 + Math.sqrt(5)) / 2;
// PNG export renders at this 3x of the on-screen SVG's CSS pixel size thus looks cripsy:)
const PNG_EXPORT_SCALE = 3;

const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const THIRD_PARTY_RE = /manager|proxy|expert/i;
const SELF_ASSESSED_RE = /self|manager/i;
const NO_INDIVIDUAL_PAIRING_RE = /manager/i;

// ── Utility functions ─────────────────────────────────────────────────────────

const fmtN = (n) => (n == null ? '' : LocaleService.fmtLocaleNumeral(n));

function areaOpacityFor(count) {
  return Math.max(0.01, 0.5 / Math.sqrt(count));
}

// Uses ~ golden ratio for the plot area (inside the axes) — the PAD_* margins
// for axis labels and the legend sit outside it, so it never reads as squished.
const PAD_HORIZONTAL = PAD_LEFT + PAD_RIGHT;
const PAD_VERTICAL = PAD_TOP + PAD_BOTTOM;
const layoutHeight = (layoutW) =>
  Math.round((layoutW - PAD_HORIZONTAL) / PHI) + PAD_VERTICAL;

// On-screen width: fills the container, but the whole SVG stays within 65% of
// screen height so users can see title and filter options without scrolling ... unless they hit the min height
// Up to LAYOUT_MAX_PX height = golden plot area + padding; beyond it the chart
// only scales, so its aspect ratio is fixed from there on.
function computeDisplayWidth(containerWidth) {
  const maxH = window.innerHeight * 0.65;
  const layoutW = (maxH - PAD_VERTICAL) * PHI + PAD_HORIZONTAL;
  const heightBudget =
    layoutW <= LAYOUT_MAX_PX
      ? layoutW
      : maxH * (LAYOUT_MAX_PX / layoutHeight(LAYOUT_MAX_PX));
  return Math.max(
    300,
    Math.min(containerWidth, Math.max(heightBudget, HEIGHT_CAP_MIN_PX))
  );
}

function isBellShaped(agg) {
  const maxVal = Math.max(...agg);
  if (maxVal === 0) return false;
  const peakIdx = agg.indexOf(maxVal);
  if (peakIdx < 2 || peakIdx > 7) return false;
  let leftViolations = 0;
  for (let i = 1; i <= peakIdx; i++) {
    if (agg[i] < agg[i - 1]) leftViolations++;
  }
  let rightViolations = 0;
  for (let i = peakIdx + 1; i < 10; i++) {
    if (agg[i] > agg[i - 1]) rightViolations++;
  }
  return leftViolations <= 1 && rightViolations <= 1;
}

function buildPath(distribution, xScale, yScale, chartHeight, padTop) {
  if (!distribution || distribution.length !== 10) return '';
  const pts = distribution.map((n, i) => [
    xScale(i),
    padTop + chartHeight - yScale(n),
  ]);
  const baseline = padTop + chartHeight;
  let d = `M ${pts[0][0]} ${baseline} L ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const dx = x1 - x0;
    d += ` C ${x0 + dx / 3} ${y0}, ${x1 - dx / 3} ${y1}, ${x1} ${y1}`;
  }
  d += ` L ${pts[9][0]} ${baseline} Z`;
  return d;
}

function splitLabel(text, maxChars = 45) {
  if (!text || text.length <= maxChars) return [text];
  const mid = Math.floor(text.length / 2);
  for (let d = 0; d < mid; d++) {
    if (text[mid + d] === ' ')
      return [text.slice(0, mid + d).trim(), text.slice(mid + d).trim()];
    if (text[mid - d] === ' ')
      return [text.slice(0, mid - d).trim(), text.slice(mid - d).trim()];
  }
  return [text];
}

function generatePairsFromPlans(plans) {
  const allPairs = new Map();
  plans.forEach((plan) => {
    if (!plan.preDistribution) return;
    const pre = [];
    const post = [];
    plan.preDistribution.forEach((c, i) => {
      for (let k = 0; k < c; k++) pre.push(i + 1);
    });
    plan.distribution.forEach((c, i) => {
      for (let k = 0; k < c; k++) post.push(i + 1);
    });
    pre.sort((a, b) => a - b);
    post.sort((a, b) => a - b);
    const n = Math.min(pre.length, post.length);
    for (let i = 0; i < n; i++) {
      const key = `${pre[i]}·${post[i]}`;
      allPairs.set(key, (allPairs.get(key) || 0) + 1);
    }
  });
  return allPairs;
}

const ALL_PAIRS = (() => {
  const rows = [];
  for (let b = 1; b <= 10; b++) {
    for (let a = b; a <= 10; a++) {
      rows.push({ b, a, diagonal: a === b });
    }
  }
  return rows;
})();

function aggregatePlans(activities, selectedMethods, selectedPlanIds) {
  return (activities || []).flatMap((a) => {
    const methodGroups = (a.methods || []).filter((p) =>
      selectedMethods.has(p.method)
    );
    if (methodGroups.length === 0) return [];

    // Aggregate at the individual evaluation-plan level, so plan-level filtering can toggle any single plan in/out without needing a refetch.
    const survivingPlans = [];
    methodGroups.forEach((m) => {
      (m.evaluationPlans || []).forEach((plan) => {
        if (selectedPlanIds && !selectedPlanIds.has(plan.id)) return;
        survivingPlans.push(plan);
      });
    });
    if (survivingPlans.length === 0) return [];

    const distribution = Array(10).fill(0);
    const hasPrePlans = survivingPlans.some((p) => p.preDistribution);
    const preDistribution = hasPrePlans ? Array(10).fill(0) : null;
    let respondents = 0;
    survivingPlans.forEach((p) => {
      (p.distribution || []).forEach((v, i) => {
        distribution[i] += v;
      });
      respondents += p.respondents || 0;
      if (preDistribution && p.preDistribution) {
        p.preDistribution.forEach((v, i) => {
          preDistribution[i] += v;
        });
      }
    });
    const count = distribution.reduce((s, v) => s + v, 0);
    const total = distribution.reduce((s, v, i) => s + v * (i + 1), 0);
    const participantsEvaluated =
      survivingPlans.reduce((s, p) => s + (p.participants || 0), 0) ||
      respondents;
    return [
      {
        id: a.id,
        name: a.name,
        statusName: a.statusName,
        distribution,
        preDistribution,
        respondents: count > 0 ? count : respondents,
        participantsEvaluated,
        avgRating: count > 0 ? Math.round((total / count) * 10) / 10 : null,
        plans: survivingPlans,
      },
    ];
  });
}

function buildProseSummary(
  t,
  activities,
  outcomeName,
  methodRespondents,
  isPrePost,
  allMethods,
  hasManagerAssessed
) {
  if (!activities || activities.length === 0) return null;
  const totalRespondents = activities.reduce((s, a) => s + a.respondents, 0);
  if (totalRespondents === 0) return null;

  const agg = Array(10).fill(0);
  activities.forEach((a) =>
    a.distribution.forEach((v, i) => {
      agg[i] += v;
    })
  );

  const weightedSum = agg.reduce((s, v, i) => s + v * (i + 1), 0);
  const avg = Math.round((weightedSum / totalRespondents) * 10) / 10;

  const firstNZ = agg.findIndex((v) => v > 0);
  const lastNZ = 9 - [...agg].reverse().findIndex((v) => v > 0);
  const lowestRating = firstNZ + 1;
  const highestRating = lastNZ + 1;
  const spread = highestRating - lowestRating;

  const highCount = agg.slice(6).reduce((a, b) => a + b, 0);
  const lowCount = agg.slice(0, 5).reduce((a, b) => a + b, 0);
  const highPct = Math.round((highCount / totalRespondents) * 100);
  const lowPct = Math.round((lowCount / totalRespondents) * 100);

  const n = activities.length;
  const avgZone =
    avg >= 7
      ? t('charts.data_zones.upper_third')
      : avg >= 4
        ? t('charts.data_zones.middle_range')
        : t('charts.data_zones.lower_third');

  const parts = [];
  const smallSample = totalRespondents <= 6;

  // Respondent and evaluation-plan counts already appear on the stats line
  // above the chart (see StatsLine) — restating them here was pure noise.
  // The one thing not already on the stats line is how many activities the
  // responses are spread across, and only when that's genuinely informative
  // (2+) — "across one activity" was trivially obvious in a single-activity
  // view.
  if (n > 1) {
    parts.push(t('charts.responses_come_from', { num: fmtN(n) }));
  }
  if (smallSample) {
    parts.push(t('charts.samples_small_and_indicative_only'));
  }

  const distWord = hasManagerAssessed
    ? t('charts.assessments')
    : t('report.responses');
  const distWordLower = distWord.toLowerCase();

  if (spread <= 2) {
    parts.push(
      t('charts.reponses_tightly_grouped', {
        distWord,
        lowestRating,
        highestRating,
      })
    );
  } else if (lowestRating >= 7) {
    parts.push(
      t('charts.all_fall_in_upper_range', {
        distWordLower,
        lowestRating,
        highestRating,
      })
    );
  } else if (highPct >= 75) {
    parts.push(
      t('charts.pct_of_responses_in_7_to_10_range', { highPct, distWordLower })
    );
  } else if (lowPct >= 75) {
    parts.push(
      t('charts.pct_of_responses_in_lower_half', { lowPct, distWordLower })
    );
  } else {
    parts.push(
      t('charts.responses_range_from_to', {
        distWord,
        lowestRating,
        highestRating,
      })
    );
  }

  const averageSelfEvident = spread <= 2 || isBellShaped(agg);
  if (!isPrePost && totalRespondents >= 5 && !averageSelfEvident) {
    parts.push(t('charts.avg_rating_sits', { avg, avgZone }));
  }

  if (isPrePost) {
    const preAgg = Array(10).fill(0);
    let preTotal = 0;
    activities.forEach((a) => {
      if (a.preDistribution) {
        a.preDistribution.forEach((v, i) => {
          preAgg[i] += v;
        });
        preTotal += a.preDistribution.reduce((s, v) => s + v, 0);
      }
    });
    if (preTotal > 0) {
      const preAvgRaw =
        preAgg.reduce((s, v, i) => s + v * (i + 1), 0) / preTotal;
      const preAvg = Math.round(preAvgRaw * 10) / 10;
      const delta = avg - preAvg;
      const absDelta = Math.round(Math.abs(delta) * 10) / 10;
      const pctChange = Math.round((Math.abs(delta) / preAvg) * 100);
      const direction =
        delta >= 0
          ? t('charts.data_direction.increase')
          : t('charts.data_direction.decrease');
      const magnitude =
        absDelta < 1 ? 'small' : absDelta <= 2 ? 'significant' : 'large';
      parts.push(
        t('charts.avg_post_rating_compares', {
          avg,
          preAvg,
          magnitude,
          increaseOrDecrease: direction,
          pctChange,
        })
      );
    } else if (totalRespondents >= 5 && !averageSelfEvident) {
      parts.push(t('charts.avg_post_rating_sits', { avg, avgZone }));
    }
  }

  if (methodRespondents.size > 0) {
    let selfCount = 0;
    methodRespondents.forEach((count, method) => {
      if (SELF_ASSESSED_RE.test(method)) selfCount += count;
    });
    const selfPct = Math.round((selfCount / totalRespondents) * 100);
    if (selfPct >= 25) {
      const hasNonManagerMethod = allMethods.some(
        (m) => !SELF_ASSESSED_RE.test(m)
      );
      const pctLabel = hasNonManagerMethod
        ? t('charts.nearly_100_pct')
        : t('charts.100_pct');
      parts.push(t('activity_manager_description', { percentage: pctLabel }));
    }
  }

  return parts;
}

// ── AvgLine sub-component ─────────────────────────────────────────────────────

function AvgLine({ x, top, bottom, label, color, fs }) {
  const labelFS = Math.round(fs * 0.85);
  const labelHalf = Math.round((label.length * labelFS * 0.58) / 2);
  const labelCY = top + labelHalf + 5;
  return (
    <g>
      <line x1={x} y1={top} x2={x} y2={bottom} stroke={color} strokeWidth={2} />
      <g transform={`translate(${x + 9}, ${labelCY})`}>
        <rect
          x={-(labelFS / 2 + 2)}
          y={-(labelHalf + 3)}
          width={labelFS + 4}
          height={labelHalf * 2 + 6}
          fill="rgba(255,255,255,0.5)"
          rx={2}
        />
        <text
          transform="rotate(-90)"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={labelFS}
          fontWeight={600}
          fill={color}
          style={{ userSelect: 'none' }}
        >
          {label}
        </text>
      </g>
    </g>
  );
}

// ── StatsLine — compact noun-phrase stats display ─────────────────────────────

function StatsLine({
  plans,
  participants,
  respondents,
  showPlans = true,
  multiseries = false,
  seriesCount = 0,
  hasManagerAssessed = false,
}) {
  const { t } = useTranslation();
  const rate =
    !hasManagerAssessed && participants > 0
      ? Math.round((respondents / participants) * 100)
      : null;
  const parts = [];
  if (multiseries) {
    parts.push({
      v: seriesCount.toLocaleString(),
      l: t('charts.multi_stage_evaluations_plural', { count: seriesCount }),
    });
    parts.push({
      v: plans.toLocaleString(),
      l: t('charts.stages_plural', { count: plans }),
    });
  } else if (showPlans && plans > 1)
    parts.push({
      v: plans.toLocaleString(),
      l: t('charts.evaluations_plural', { count: plans }),
    });
  parts.push({
    v: participants.toLocaleString(),
    l: t('charts.participants_plural_other'),
  });
  parts.push({
    v: respondents.toLocaleString(),
    l: hasManagerAssessed
      ? t('charts.participants_considered')
      : t('charts.responses'),
  });
  if (rate !== null)
    parts.push({ v: `${rate}%`, l: t('charts.response_rate') });
  return (
    <span className="or__stats-line">
      {parts.map((p, i) => (
        <span className="or__stats-item" key={p.l}>
          <strong>{p.v}</strong> {p.l}
          {i < parts.length - 1 && <span className="or__stats-comma">,</span>}
        </span>
      ))}
    </span>
  );
}

// Filter checklist allows filter by Method/Evaluation-Plan/ Multiseries
function FilterCheckList({ label, onSelectAll, items }) {
  const { t } = useTranslation();

  return (
    <div className="or__filter-section">
      <div className="or__filter-section-header">
        <span className="or__method-label">{label}</span>
        <span className="or__activities-allnone">
          <button type="button" onClick={onSelectAll} className="or__link-btn">
            {t('actions.select_all_lower')}
          </button>
        </span>
      </div>
      <div className="or__filter-checks">
        {items.map(
          ({ key, checked, isUnavailable, onChange, displayLabel }) => (
            <label
              key={key}
              className={`or__method-check${
                isUnavailable ? ' or__method-check--unavailable' : ''
              }`}
              title={
                isUnavailable
                  ? t('charts.not_available_for_individual_graph')
                  : undefined
              }
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={isUnavailable}
                onChange={onChange}
                style={{
                  cursor: isUnavailable ? 'not-allowed' : 'pointer',
                  accentColor: `rgb(${BASE_COLOR})`,
                }}
              />
              {displayLabel}
            </label>
          )
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

// ── Inter font embedding for PNG export ─────────────────────────────────────
// The capture below blob-renders the SVG via an <img>, which can't see the
// page's Google Fonts <link> — so to use Inter in the exported PNG it has to
// be embedded directly as a self-contained @font-face with base64 font data.
// Fetched once and cached for the session; captureSVGasPNG falls back to
// Arial if this fails for any reason (e.g. offline).
let interFontFaceCSSPromise = null;

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

async function fetchEmbeddedInterFontFaceCSS() {
  const cssRes = await fetch(
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap'
  );
  if (!cssRes.ok) throw new Error('Inter CSS fetch failed');
  const cssText = await cssRes.text();

  const blocks = cssText.match(/@font-face\s*{[^}]*}/g) || [];
  const latinBlocks = blocks.filter((b) => b.includes('U+0000-00FF'));
  if (latinBlocks.length === 0)
    throw new Error('No Inter latin font-face blocks found');

  const embedded = await Promise.all(
    latinBlocks.map(async (block) => {
      const urlMatch = block.match(
        /url\((https:\/\/fonts\.gstatic\.com\/[^)]+)\)/
      );
      if (!urlMatch) return block;
      const fontRes = await fetch(urlMatch[1]);
      if (!fontRes.ok) throw new Error('Inter font file fetch failed');
      const buffer = await fontRes.arrayBuffer();
      const base64 = arrayBufferToBase64(buffer);
      return block.replace(urlMatch[1], `data:font/woff2;base64,${base64}`);
    })
  );

  return embedded.join('\n');
}

function getEmbeddedInterFontFaceCSS() {
  if (!interFontFaceCSSPromise) {
    interFontFaceCSSPromise = fetchEmbeddedInterFontFaceCSS().catch((e) => {
      console.error(
        'Inter font embedding failed, falling back to Arial for PNG export',
        e
      );
      interFontFaceCSSPromise = null; // allow retry on next export
      return null;
    });
  }
  return interFontFaceCSSPromise;
}

// Captures the SVG inside a container element as a PNG data URL. Uses Inter to match the on-screen chart; falls back to Arial if the font embedding fails for any reason.
async function captureSVGasPNG(t, containerEl) {
  const svg = containerEl?.querySelector('svg');
  if (!svg) throw new Error(t('charts.errors.no_svg_found'));

  const embeddedFontCSS = await getEmbeddedInterFontFaceCSS();
  const CAPTURE_FONT = embeddedFontCSS
    ? "'Inter', Arial, Helvetica, sans-serif"
    : 'Arial, Helvetica, sans-serif';

  const textEls = Array.from(svg.querySelectorAll('text, tspan'));
  const origFonts = textEls.map((el) => el.style.fontFamily);
  textEls.forEach((el) => {
    el.style.fontFamily = CAPTURE_FONT;
  });

  let styleEl = null;
  if (embeddedFontCSS) {
    styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
    styleEl.textContent = embeddedFontCSS;
    svg.insertBefore(styleEl, svg.firstChild);
  }

  // ensure the crosshair hover hidden on png save
  const crosshairEl = svg.querySelector('.or__hover-crosshair');
  const origCrosshairDisplay = crosshairEl?.style.display;
  if (crosshairEl) crosshairEl.style.display = 'none';

  const svgData = new XMLSerializer().serializeToString(svg);

  // Restore live fonts + remove the injected style immediately so the visible chart is unaffected.
  textEls.forEach((el, i) => {
    el.style.fontFamily = origFonts[i];
  });
  if (styleEl) styleEl.remove();
  if (crosshairEl) crosshairEl.style.display = origCrosshairDisplay || '';

  const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const naturalWidth =
        svg.width?.baseVal?.value || svg.viewBox?.baseVal?.width || 680;
      const naturalHeight =
        svg.height?.baseVal?.value || svg.viewBox?.baseVal?.height || 420;
      const canvas = document.createElement('canvas');
      canvas.width = naturalWidth * PNG_EXPORT_SCALE;
      canvas.height = naturalHeight * PNG_EXPORT_SCALE;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

// Wrapped in forwardRef so a parent that hides the built-in "Include in
// report" toggle (showIncludeToggle=false) — e.g. the Evaluation page's own
// legacy toggle — can still request a PNG snapshot of whatever's currently
// on screen via ref.current.capturePng(), instead of duplicating the
// capture logic the internal toggle button already has.
const ActivityOutcomeRatingsInner = (
  {
    activityId,
    outcomeId,
    prefetchedData,
    evaluationPlanIds,
    refreshToken = 0,
    showIncludeToggle = true,
    initialIncluded = false, // seed included state from parent (e.g. restored from DB)
    capturedPng = null, // base64 PNG data URL — when set, shown instead of live SVG
    onIncluded, // (pngDataUrl: string) => void — called when Include = Yes
    onExcluded, // () => void — called when Include = No
    onProse, // (text: string | null) => void — called when prose is generated
    showProse = true, // set false where a separate editable Analysis field already
    // covers this (e.g. evaluationQuestion.js's NarrativeCKEditor) or where the
    // saved reportNarrative should be shown instead of the live-computed prose
    // (e.g. the Report tab) — prose is still computed/passed to onProse either way
    onHasData, // (hasData: boolean) => void — called once the fetch resolves, so a parent rendering its own content above this chart (e.g. Results header) can hide it when there's nothing to show
    onChartModeChange, // ('trend' | 'individual') => allows a seperate component (e.g. an "include in report" toggle) can track which mode is currently displayed
    hideNoDataMessage = false, // suppress the built-in "no responses" box — for callers that render several instances per evaluation plan and want to show a single shared message instead (via onHasData)
    savePngLabel = null, // e.g. "Finding Stories · Food stories"
    savePngFilename = null, // e.g. "finding-stories--food-stories--creativity-inspired"
    planName = null,
    visibleModes = null,
    reportMode = false, // Read-only rendering for the Reports tab: hides the filter panel and Download buttons:)
    initialFilterState = null,
    onFilterChange,
  },
  ref
) => {
  const { t } = useTranslation();
  const [data, setData] = useState(prefetchedData ?? null);
  const [loading, setLoading] = useState(!prefetchedData);
  const [error, setError] = useState(null);
  const [included, setIncluded] = useState(initialIncluded);

  useEffect(() => {
    setIncluded(Boolean(capturedPng));
  }, [capturedPng]);
  const [capturing, setCapturing] = useState(false);
  const [savingPng, setSavingPng] = useState(false);
  const [chartMode, setChartMode] = useState('trend');
  const [selectedMethods, setSelectedMethods] = useState(new Set());
  const [selectedPlanIds, setSelectedPlanIds] = useState(new Set());
  const userInteractedRef = useRef(false);
  // Method and plan filters are mutually exclusive. e.g you can filter by method OR eval plan (OR multiseries if avaliable). So if a user has a filter then twitches tabs, it resets the new filter type to all
  const [filterTab, setFilterTab] = useState('method');
  // Legend click-to-toggle - ('before' | 'avgBefore' | 'after' | 'avgAfter').
  const [hiddenSeries, setHiddenSeries] = useState(new Set());
  const toggleSeries = useCallback((key) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  // Hover crosshair + tooltip (trend mode only)
  const [hoverRating, setHoverRating] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [hoverPair, setHoverPair] = useState(null);
  const [svgWidth, setSvgWidth] = useState(() =>
    computeDisplayWidth(window.innerWidth * 0.96)
  );

  const svgRef = useRef(null);
  const svgRefIndividual = useRef(null);

  // ── Fixed-size export render ──
  // PNG exports (Show in report, Download graph, capturePng) are captured
  // from a hidden offscreen copy of the chart laid out at LAYOUT_MAX_PX, not
  // the on-screen SVG — so every export is the same size and layout whatever
  // screen it was made on. The copy only mounts for the capture.
  const [exportMode, setExportMode] = useState(null);
  const exportResolveRef = useRef(null);
  const exportQueueRef = useRef(Promise.resolve());
  const exportWrapRef = useCallback((el) => {
    if (el && exportResolveRef.current) {
      const resolve = exportResolveRef.current;
      exportResolveRef.current = null;
      resolve(el);
    }
  }, []);
  const captureForExport = useCallback((mode) => {
    // Serialised: the copy renders one mode at a time, and a second request
    // for an already-mounted mode would never re-fire the ref callback.
    const run = exportQueueRef.current.then(async () => {
      const el = await new Promise((resolve) => {
        exportResolveRef.current = resolve;
        setExportMode(mode);
      });
      try {
        return await captureSVGasPNG(t, el);
      } finally {
        setExportMode(null);
      }
    });
    exportQueueRef.current = run.catch(() => {});
    return run;
  }, []);

  // Same capture path the built-in "Include in report" button uses —
  // exposed so a parent that has hidden that button (showIncludeToggle=false)
  // can still snapshot the Trend chart on demand.
  useImperativeHandle(
    ref,
    () => ({
      capturePng: () =>
        svgRef.current ? captureForExport('trend') : Promise.resolve(null),
    }),
    [captureForExport]
  );

  const instanceId = useId();
  const chartModeName = `or-chart-mode-${instanceId}`;

  // Use prefetched data from parent when available (or when it arrives after mount)
  useEffect(() => {
    if (!prefetchedData) return;
    setData(prefetchedData);
    setLoading(false);
    setError(null);
  }, [prefetchedData]);

  // Fetch own data only when no prefetched data is provided
  useEffect(() => {
    if (prefetchedData) return;
    if (!activityId || !outcomeId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = { outcomeId };
    if (evaluationPlanIds && evaluationPlanIds.length > 0) {
      params.evaluationPlanIds = evaluationPlanIds.join(',');
    }

    axios
      .get(`/project/${activityId}/outcome-ratings`, { params })
      .then((res) => {
        if (cancelled) return;
        const j = res.data;
        if (!j?.ok)
          throw new Error(j?.error || t('charts.errors.error_loading_ratings'));
        setData(j);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // evaluationPlanIds is often passed as a fresh array literal by the caller
    // on every render — depend on its joined value so an unrelated parent
    // re-render (e.g. a sibling chart's toggle) doesn't trigger a refetch.
    // refreshToken is bumped by the parent on tab-visibility-regain, since
    // this component otherwise never refetches once mounted.
  }, [
    activityId,
    outcomeId,
    prefetchedData,
    (evaluationPlanIds || []).join(','),
    refreshToken,
  ]);

  const onHasDataRef = useRef(onHasData);
  onHasDataRef.current = onHasData;
  useEffect(() => {
    if (loading) return;
    onHasDataRef.current?.(Boolean(data) && (data.activities || []).length > 0);
  }, [loading, data]);

  const onChartModeChangeRef = useRef(onChartModeChange);
  onChartModeChangeRef.current = onChartModeChange;
  useEffect(() => {
    onChartModeChangeRef.current?.(chartMode);
  }, [chartMode]);

  const initialFilterStateRef = useRef(initialFilterState);
  initialFilterStateRef.current = initialFilterState;

  // Initialise selected methods when data arrives — seeded from a
  // persisted filter when provided, falling back to "select everything" otherwise. Resets
  // userInteractedRef so this automatic reseed is never mistaken for a real user edit by the save effect
  useEffect(() => {
    if (!data?.methods) return;
    userInteractedRef.current = false;
    const available = data.methods;
    const persisted = (initialFilterStateRef.current?.methods || []).filter(
      (m) => available.includes(m)
    );
    setSelectedMethods(new Set(persisted.length > 0 ? persisted : available));
  }, [data]);

  useEffect(() => {
    if (!data?.activities) return;
    userInteractedRef.current = false;
    const planIds = new Set();
    data.activities.forEach((a) =>
      (a.methods || []).forEach((m) =>
        (m.evaluationPlans || []).forEach((p) => planIds.add(p.id))
      )
    );
    const persisted = (
      initialFilterStateRef.current?.evaluationPlanIds || []
    ).filter((id) => planIds.has(id));
    setSelectedPlanIds(persisted.length > 0 ? new Set(persisted) : planIds);
  }, [data]);

  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;
  useEffect(() => {
    if (!userInteractedRef.current) return;
    const t = setTimeout(() => {
      onFilterChangeRef.current?.({
        methods: [...selectedMethods],
        evaluationPlanIds: [...selectedPlanIds],
      });
    }, 500);
    return () => clearTimeout(t);
  }, [selectedMethods, selectedPlanIds]);

  // Measure container width.
  const [measureEl, setMeasureEl] = useState(null);
  useEffect(() => {
    const el = measureEl;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setSvgWidth(computeDisplayWidth(w));
    };
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    measure();
    window.addEventListener('resize', measure);
    return () => {
      obs.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measureEl]);

  const activities = useMemo(
    () => aggregatePlans(data?.activities, selectedMethods, selectedPlanIds),
    [data, selectedMethods, selectedPlanIds]
  );

  // Individual mode shouldnt include manager-assessed...
  const individualModeMethods = useMemo(() => {
    const s = new Set();
    selectedMethods.forEach((m) => {
      if (!NO_INDIVIDUAL_PAIRING_RE.test(m)) s.add(m);
    });
    return s;
  }, [selectedMethods]);
  const individualActivities = useMemo(
    () =>
      aggregatePlans(data?.activities, individualModeMethods, selectedPlanIds),
    [data, individualModeMethods, selectedPlanIds]
  );

  // Full, unfiltered list of methods/plans available to filter by.
  const allMethods = data?.methods || [];
  const planMethodById = useMemo(() => {
    const map = new Map();
    (data?.activities || []).forEach((a) =>
      (a.methods || []).forEach((m) =>
        (m.evaluationPlans || []).forEach((p) => {
          if (!map.has(p.id)) map.set(p.id, m.method);
        })
      )
    );
    return map;
  }, [data]);
  const allPlans = useMemo(() => {
    const map = new Map();
    (data?.activities || []).forEach((a) =>
      (a.methods || []).forEach((m) =>
        (m.evaluationPlans || []).forEach((p) => {
          if (!map.has(p.id)) map.set(p.id, p);
        })
      )
    );
    return Array.from(map.values());
  }, [data]);

  // A multi-stage evaluation ("series") all point at the first in series
  const seriesGroups = useMemo(() => {
    const groups = new Map();
    allPlans.forEach((p) => {
      const key = p.seriesParentId || p.id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    return Array.from(groups.entries())
      .filter(([, plans]) => plans.some((p) => p.seriesParentId))
      .map(([key, plans]) => {
        const sorted = [...plans].sort(
          (a, b) => (a.seriesPosition || 0) - (b.seriesPosition || 0)
        );
        const root = plans.find((p) => p.id === key);
        const label =
          (root || sorted[0])?.name?.replace(/\s*\(\d+\)$/, '') ||
          `${t('charts.series')} ${key}`;
        return { key, label, plans: sorted };
      });
  }, [allPlans]);
  const hasMultiseries = seriesGroups.length > 0;

  const showMethodTab = allMethods.length > 1;
  const tabsAvailable = [
    showMethodTab && 'method',
    'plan',
    hasMultiseries && 'multiseries',
  ].filter(Boolean);
  const activeFilterTab = tabsAvailable.includes(filterTab)
    ? filterTab
    : 'plan';

  const isMultiseriesView =
    hasMultiseries &&
    selectedPlanIds.size > 0 &&
    [...selectedPlanIds].every((id) =>
      seriesGroups.some((g) => g.plans.some((p) => p.id === id))
    );
  // Whether the current selection is a strict subset of everything
  // available — drives both the popover's filter badge and, in
  // report tab (ReportMode), the read-only "a filter is applied" note
  const isFiltered =
    selectedMethods.size < allMethods.length ||
    selectedPlanIds.size < allPlans.length;
  // Description of what filter is applies in eval tab, so on reports page it tells users what plans contribute to summary graph
  const filterKindLabel = isMultiseriesView
    ? t('charts.multi_series_selection')
    : selectedMethods.size < allMethods.length
      ? t('charts.method').toLowerCase()
      : t('charts.evaluation_plans.evaluation_plan');
  const includedPlanNames = allPlans
    .filter(
      (p) =>
        selectedPlanIds.has(p.id) &&
        selectedMethods.has(planMethodById.get(p.id))
    )
    .map((p) => p.name || `${t('charts.evaluation_plans.plan')} ${p.id}`);

  const ratingAgg = useMemo(() => {
    const postAgg = Array(10).fill(0);
    activities.forEach((a) =>
      a.distribution.forEach((v, i) => {
        postAgg[i] += v;
      })
    );
    const postTotal = postAgg.reduce((s, v) => s + v, 0);
    const postAvg =
      postTotal > 0
        ? postAgg.reduce((s, v, i) => s + v * (i + 1), 0) / postTotal
        : null;

    const preAgg = Array(10).fill(0);
    let hasPreDataRaw = false;
    activities.forEach((a) => {
      if (a.preDistribution) {
        a.preDistribution.forEach((v, i) => {
          preAgg[i] += v;
          if (v > 0) hasPreDataRaw = true;
        });
      }
    });
    const hasPreData = Boolean(data?.isPrePost) && hasPreDataRaw;
    const preTotal = preAgg.reduce((s, v) => s + v, 0);
    const preAvg =
      hasPreData && preTotal > 0
        ? preAgg.reduce((s, v, i) => s + v * (i + 1), 0) / preTotal
        : null;

    return { postAgg, preAgg, hasPreData, postAvg, preAvg };
  }, [activities, data]);

  const totalRespondents = activities.reduce((s, a) => s + a.respondents, 0);
  const totalParticipants = activities.reduce(
    (s, a) => s + (a.participantsEvaluated || a.respondents),
    0
  );
  const totalPlans = useMemo(() => {
    const planIds = new Set();
    (data?.activities || []).forEach((a) => {
      (a.methods || [])
        .filter((p) => selectedMethods.has(p.method))
        .forEach((p) => {
          (p.evaluationPlans || []).forEach((ep) => {
            if (selectedPlanIds.has(ep.id)) planIds.add(ep.id);
          });
        });
    });
    return planIds.size;
  }, [data, selectedMethods, selectedPlanIds]);

  const individualTotalRespondents = individualActivities.reduce(
    (s, a) => s + a.respondents,
    0
  );
  const individualTotalParticipants = individualActivities.reduce(
    (s, a) => s + (a.participantsEvaluated || a.respondents),
    0
  );
  const individualTotalPlans = useMemo(() => {
    const planIds = new Set();
    (data?.activities || []).forEach((a) => {
      (a.methods || [])
        .filter((p) => individualModeMethods.has(p.method))
        .forEach((p) => {
          (p.evaluationPlans || []).forEach((ep) => {
            if (selectedPlanIds.has(ep.id)) planIds.add(ep.id);
          });
        });
    });
    return planIds.size;
  }, [data, individualModeMethods, selectedPlanIds]);

  // How many distinct multiseries families are actually contributing to the filter
  const totalSeriesCount = useMemo(() => {
    if (!hasMultiseries) return 0;
    return seriesGroups.filter((g) =>
      g.plans.some(
        (p) =>
          selectedPlanIds.has(p.id) &&
          selectedMethods.has(planMethodById.get(p.id))
      )
    ).length;
  }, [
    hasMultiseries,
    seriesGroups,
    selectedPlanIds,
    selectedMethods,
    planMethodById,
  ]);
  const individualSeriesCount = useMemo(() => {
    if (!hasMultiseries) return 0;
    return seriesGroups.filter((g) =>
      g.plans.some(
        (p) =>
          selectedPlanIds.has(p.id) &&
          individualModeMethods.has(planMethodById.get(p.id))
      )
    ).length;
  }, [
    hasMultiseries,
    seriesGroups,
    selectedPlanIds,
    individualModeMethods,
    planMethodById,
  ]);

  const getStatsForMode = (mode) =>
    mode === 'individual'
      ? {
          plans: individualTotalPlans,
          participants: individualTotalParticipants,
          respondents: individualTotalRespondents,
          seriesCount: individualSeriesCount,
        }
      : {
          plans: totalPlans,
          participants: totalParticipants,
          respondents: totalRespondents,
          seriesCount: totalSeriesCount,
        };

  const methodRespondents = useMemo(() => {
    const map = new Map();
    (data?.activities || []).forEach((a) => {
      (a.methods || []).forEach((p) => {
        if (selectedMethods.has(p.method)) {
          map.set(p.method, (map.get(p.method) || 0) + p.respondents);
        }
      });
    });
    return map;
  }, [data, selectedMethods]);

  const hasThirdParty = [...selectedMethods].some((m) =>
    THIRD_PARTY_RE.test(m)
  );
  const hasManagerAssessed = [...selectedMethods].some((m) =>
    NO_INDIVIDUAL_PAIRING_RE.test(m)
  );
  const allManagerAssessed =
    selectedMethods.size > 0 &&
    [...selectedMethods].every((m) => NO_INDIVIDUAL_PAIRING_RE.test(m));

  // If a method filter change leaves only manager-assessed methods selected while Individual is showing, fall back to Trend.
  useEffect(() => {
    if (allManagerAssessed)
      setChartMode((prev) => (prev === 'individual' ? 'trend' : prev));
  }, [allManagerAssessed]);

  // At least one method must always stay selected — otherwise the chart
  // falls into the "No data for the selected methods" dead end.
  const toggleMethod = useCallback((m) => {
    userInteractedRef.current = true;
    setSelectedMethods((prev) => {
      if (prev.has(m)) {
        if (prev.size <= 1) return prev; // can't uncheck the last one
        const next = new Set(prev);
        next.delete(m);
        return next;
      }
      const next = new Set(prev);
      next.add(m);
      return next;
    });
  }, []);
  // Internal, non-flagging — resets the method dimension to "all" as pure
  // tab-switch plumbing (see the filterTab comment above). This must NOT
  // count as a user filter edit, or merely clicking between filter tabs
  // would get persisted as if the user had chosen something.
  const resetMethodsToAll = useCallback(() => {
    setSelectedMethods(new Set(data?.methods || []));
  }, [data]);
  const selectAllMethods = useCallback(() => {
    userInteractedRef.current = true;
    resetMethodsToAll();
  }, [resetMethodsToAll]);

  // Same "at least one" floor as toggleMethod, applied to individual plans.
  const togglePlan = useCallback((planId) => {
    userInteractedRef.current = true;
    setSelectedPlanIds((prev) => {
      if (prev.has(planId)) {
        if (prev.size <= 1) return prev; // can't uncheck the last one
        const next = new Set(prev);
        next.delete(planId);
        return next;
      }
      const next = new Set(prev);
      next.add(planId);
      return next;
    });
  }, []);
  // Internal, non-flagging — same tab-switch-plumbing rule as
  // resetMethodsToAll above.
  const resetPlansToAll = useCallback(() => {
    const planIds = new Set();
    (data?.activities || []).forEach((a) =>
      (a.methods || []).forEach((m) =>
        (m.evaluationPlans || []).forEach((p) => planIds.add(p.id))
      )
    );
    setSelectedPlanIds(planIds);
  }, [data]);
  const selectAllPlans = useCallback(() => {
    userInteractedRef.current = true;
    resetPlansToAll();
  }, [resetPlansToAll]);

  // Series aggregation only — every stage in the picked series is included
  // and summed together (aggregatePlans already does this once the member
  // plan ids are in selectedPlanIds). Same "at least one" floor: a series
  // can't be unchecked if it's the only one currently selected.
  const toggleSeriesGroup = useCallback((groupPlanIds) => {
    userInteractedRef.current = true;
    setSelectedPlanIds((prev) => {
      const allIn = groupPlanIds.every((id) => prev.has(id));
      if (allIn) {
        const next = new Set(prev);
        groupPlanIds.forEach((id) => next.delete(id));
        if (next.size === 0) return prev; // can't uncheck the last series
        return next;
      }
      const next = new Set(prev);
      groupPlanIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const resetSeriesToAll = useCallback(() => {
    const planIds = new Set();
    seriesGroups.forEach((g) => g.plans.forEach((p) => planIds.add(p.id)));
    setSelectedPlanIds(planIds);
  }, [seriesGroups]);
  const selectAllSeries = useCallback(() => {
    userInteractedRef.current = true;
    resetSeriesToAll();
  }, [resetSeriesToAll]);

  // ── SVG layout ──
  const maxY =
    activities.length > 0
      ? Math.max(
          1,
          ...activities.flatMap((a) => [
            ...(a.distribution || []),
            ...(a.preDistribution || []),
          ])
        )
      : 1;
  const yMax = Math.ceil(maxY / 5) * 5 || 5;
  const yTicks = Array.from({ length: 6 }, (_, i) =>
    Math.round((yMax / 5) * i)
  );
  const fmtTick = (v) => (v >= 1000 ? (v / 1000).toFixed(1) + 'k' : String(v));

  // `W` × `SVG_HEIGHT` is the layout (viewBox) size; `displayW` × `displayH`
  // is the size it's drawn at — the same up to LAYOUT_MAX_PX, scaled beyond.
  const getLayout = (displayW) => {
    const W = Math.min(displayW, LAYOUT_MAX_PX);
    const chartW = W - PAD_HORIZONTAL;
    const chartH = Math.round(chartW / PHI);
    const SVG_HEIGHT = chartH + PAD_VERTICAL;
    return {
      displayW,
      displayH: Math.round((SVG_HEIGHT * displayW) / W),
      W,
      SVG_HEIGHT,
      chartW,
      chartH,
      FS: Math.max(
        12,
        Math.min(16, Math.round(12 + ((chartW - 600) * 4) / 500))
      ),
      xScale: (i) => PAD_LEFT + (i / 9) * chartW,
      yScale: (n) => (n / yMax) * chartH,
      colW: chartW / 10,
    };
  };
  const screenLayout = getLayout(svgWidth);
  const exportLayout = getLayout(LAYOUT_MAX_PX);

  const outcomeName =
    data?.outcomeName || `${t('common.outcome')} ${outcomeId}`;
  // graphLabel is the title minus its trailing verb/qualifier (e.g. "Creativity
  // stimulated" -> "Creativity") - for some objectives this is same as short label, for others slightly different...
  const displayName =
    data?.outcomeGraphLabel ||
    data?.outcomeShortLabel ||
    displayOutcome(outcomeName, data?.outcomeId || outcomeId);

  const prose = buildProseSummary(
    t,
    activities,
    outcomeName,
    methodRespondents,
    data?.isPrePost ?? false,
    data?.methods || [],
    hasManagerAssessed
  );

  // Fire onProse once when prose first becomes available (data has loaded).
  // Skipping null avoids a spurious state update on every chart instance at mount.
  // Passes an HTML bullet list so reportNarrative is stored and rendered as bullets.
  const onProseRef = useRef(onProse);
  onProseRef.current = onProse;
  useEffect(() => {
    if (prose != null && prose.length > 0) {
      const html =
        '<ul>' + prose.map((p) => `<li>${p}</li>`).join('') + '</ul>';
      onProseRef.current?.(html);
    }
  }, [prose]);

  // ── Save PNG  ────────────────────────────────────

  const handleSavePng = useCallback(
    async (
      mode,
      statsForPng = {
        plans: totalPlans,
        participants: totalParticipants,
        respondents: totalRespondents,
      },
      multiseriesForPng = isMultiseriesView,
      hasManagerAssessedForPng = modes[0] === 'trend' && hasManagerAssessed
    ) => {
      setSavingPng(true);
      try {
        const chartDataUrl = await captureForExport(mode);
        const chartImg = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = chartDataUrl;
        });

        // Header sizes are in chart-layout px (× S = image px). The export is
        // always laid out at LAYOUT_MAX_PX, so these are sized for that width.
        const S = PNG_EXPORT_SCALE;
        const W = chartImg.width;
        // Title + stats start where the y-axis tick labels do — same 50px
        // inset as the on-screen .or__title / .or__stats (activity.scss).
        const PAD_X = 50 * S;
        const BAND_H = 36 * S; // grey identification band
        const NAME_H = 48 * S; // title row
        const STATS_H = 36 * S; // participants / responses row
        const MARGIN_Y = 28 * S; // whitespace above the header + below the chart
        const TITLE_FS = 26 * S;
        const STATS_FS = 17 * S;
        const HEADER_FONT = "'Inter', system-ui, sans-serif";
        const bandH = savePngLabel ? BAND_H : 0;
        // Title always draws (falls back to the outcome's display name when no
        // explicit planName is passed), matching the on-screen title + stats bar.
        const headingText = planName || displayName;
        const headTop = bandH + MARGIN_Y;
        const headerH = headTop + (headingText ? NAME_H + STATS_H : 0);

        const canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = chartImg.height + headerH + MARGIN_Y;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Grey identification band
        if (savePngLabel) {
          ctx.fillStyle = '#e5e7eb';
          ctx.fillRect(0, 0, W, BAND_H);
          ctx.fillStyle = '#374151';
          ctx.font = `${13 * S}px monospace`;
          ctx.textBaseline = 'middle';
          // ctx.fillText(`[${savePngLabel} · ✂ crop]`, PAD_X, BAND_H / 2);
        }

        if (headingText) {
          // Title
          ctx.fillStyle = '#111827';
          ctx.font = `600 ${TITLE_FS}px ${HEADER_FONT}`;
          ctx.textBaseline = 'middle';
          ctx.fillText(headingText, PAD_X, headTop + NAME_H / 2);

          // ensures save actually shows whichever stats line is actually showing on screen.
          const {
            plans,
            participants,
            respondents,
            seriesCount = 0,
          } = statsForPng;
          const statsY = headTop + NAME_H + STATS_H / 2;
          ctx.textBaseline = 'middle';
          const rate =
            !hasManagerAssessedForPng && participants > 0
              ? Math.round((respondents / participants) * 100)
              : null;
          const showPlansStat = !(evaluationPlanIds?.length === 1) && plans > 1;
          const segments = [
            ...(multiseriesForPng
              ? [
                  {
                    bold: fmtN(seriesCount),
                    normal: ` ${t('charts.multi_stage_evaluations_plural', { count: seriesCount })}, `,
                  },
                  {
                    bold: fmtN(plans),
                    normal: ` ${t('charts.stages_plural', { count: plans })}, `,
                  },
                ]
              : showPlansStat
                ? [
                    {
                      bold: fmtN(plans),
                      normal: ` ${t('charts.evaluations_plural', { count: plans })}, `,
                    },
                  ]
                : []),
            {
              bold: fmtN(participants),
              normal: ` ${t('charts.participants_plural_other')}`,
            },
            {
              bold: `, ${fmtN(respondents)}`,
              normal: hasManagerAssessedForPng
                ? ` ${t('charts.participants_considered')}`
                : ` ${t('charts.responses')}`,
            },
            ...(rate !== null
              ? [
                  {
                    bold: `, ${rate}%`,
                    normal: ` ${t('charts.response_rate')}`,
                  },
                ]
              : []),
          ];
          let x = PAD_X;
          for (const seg of segments) {
            ctx.fillStyle = '#111827';
            ctx.font = `bold ${STATS_FS}px ${HEADER_FONT}`;
            ctx.fillText(seg.bold, x, statsY);
            x += ctx.measureText(seg.bold).width;
            ctx.fillStyle = '#6b7280';
            ctx.font = `${STATS_FS}px ${HEADER_FONT}`;
            ctx.fillText(seg.normal, x, statsY);
            x += ctx.measureText(seg.normal).width;
          }
        }

        // Chart below the header
        ctx.drawImage(chartImg, 0, headerH);

        const filename = savePngFilename || outcomeName || 'chart';
        const link = document.createElement('a');
        link.download = `${filename}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch (e) {
        console.error('Save PNG failed', e);
      } finally {
        setSavingPng(false);
      }
    },
    [
      captureForExport,
      savePngLabel,
      savePngFilename,
      planName,
      displayName,
      outcomeName,
      totalParticipants,
      totalRespondents,
      totalPlans,
      evaluationPlanIds,
      isMultiseriesView,
      hasManagerAssessed,
    ]
  );

  // ── Render ────────────────────────────────────────────────────────────────

  // isExport: the offscreen fixed-size copy — no hover crosshair/tooltips.
  const renderChartBlock = (mode, containerRef, layout, isExport = false) => {
    const {
      displayW,
      displayH,
      W,
      SVG_HEIGHT,
      chartW,
      chartH,
      FS,
      xScale,
      yScale,
      colW,
    } = layout;
    return (
      <>
        <div
          ref={containerRef}
          className="or__svg-wrap"
          style={
            !isExport && capturedPng && included
              ? { display: 'none' }
              : undefined
          }
        >
          <svg
            viewBox={`0 0 ${W} ${SVG_HEIGHT}`}
            width={displayW}
            height={displayH}
            preserveAspectRatio="xMidYMid meet"
            className="or__svg"
            style={{ fontFamily: "'Inter', Arial, Helvetica, sans-serif" }}
          >
            <defs>
              <marker
                id="aor-arrowHead"
                markerWidth="7"
                markerHeight="7"
                refX="6"
                refY="3.5"
                orient="auto"
              >
                <path d="M 0 0 L 7 3.5 L 0 7 Z" fill={AXIS_COLOR} />
              </marker>
            </defs>

            {/* Before/After + average legend area chart only, indiviudal doesnt need*/}
            {mode === 'trend' &&
              data?.isPrePost &&
              activities.length > 0 &&
              (() => {
                const sh = 12,
                  sw = 24,
                  fs = 12;
                const legendX = PAD_LEFT + chartW + 14;
                const centerY = PAD_TOP + chartH / 2;
                const rowGap = 20;
                const rows = [
                  {
                    key: 'before',
                    kind: 'area',
                    color: PRE_COLOR,
                    label: t('charts.before'),
                  },
                  {
                    key: 'avgBefore',
                    kind: 'line',
                    color: PRE_COLOR,
                    label: t('charts.average_before'),
                  },
                  {
                    key: 'after',
                    kind: 'area',
                    color: BASE_COLOR,
                    label: t('charts.after'),
                  },
                  {
                    key: 'avgAfter',
                    kind: 'line',
                    color: BASE_COLOR,
                    label: t('charts.average_after'),
                  },
                ];
                const offset = (rows.length - 1) / 2;
                return (
                  <g>
                    {rows.map((row, i) => {
                      const hidden = hiddenSeries.has(row.key);
                      return (
                        <g
                          key={row.key}
                          transform={`translate(${legendX}, ${
                            centerY + (i - offset) * rowGap
                          })`}
                          onClick={() => toggleSeries(row.key)}
                          style={{ cursor: 'pointer' }}
                          opacity={hidden ? 0.35 : 1}
                        >
                          {row.kind === 'area' ? (
                            <rect
                              x={0}
                              y={-sh / 2}
                              width={sw}
                              height={sh}
                              rx={2}
                              fill={`rgba(${row.color},0.6)`}
                            />
                          ) : (
                            <line
                              x1={0}
                              y1={0}
                              x2={sw}
                              y2={0}
                              stroke={`rgb(${row.color})`}
                              strokeWidth={2}
                            />
                          )}
                          <text
                            x={sw + 4}
                            y={0}
                            dominantBaseline="middle"
                            fontSize={fs}
                            fill="#6b7280"
                          >
                            {row.label}
                          </text>
                        </g>
                      );
                    })}
                  </g>
                );
              })()}

            {/* Y grid lines — trend only */}
            {mode === 'trend' &&
              yTicks.map((v) => {
                const y = PAD_TOP + chartH - yScale(v);
                return (
                  <g key={v}>
                    <line
                      x1={PAD_LEFT}
                      y1={y}
                      x2={PAD_LEFT + chartW}
                      y2={y}
                      stroke="#f3f4f6"
                      strokeWidth={1}
                    />
                    <text
                      x={PAD_LEFT - 6}
                      y={y}
                      textAnchor="end"
                      dominantBaseline="middle"
                      fontSize={Math.round(FS * 0.85)}
                      fill={AXIS_COLOR}
                    >
                      {fmtTick(v)}
                    </text>
                  </g>
                );
              })}

            {/* X axis baseline */}
            <line
              x1={PAD_LEFT}
              y1={PAD_TOP + chartH}
              x2={PAD_LEFT + chartW}
              y2={PAD_TOP + chartH}
              stroke="#e5e7eb"
              strokeWidth={1}
            />

            {/* ── Individual rate-of-change chart ── */}
            {mode === 'individual' &&
              (() => {
                const dataPairs = new Map();
                (data?.activities || []).forEach((a) => {
                  const activePlans = (a.methods || []).filter(
                    (p) =>
                      selectedMethods.has(p.method) &&
                      !NO_INDIVIDUAL_PAIRING_RE.test(p.method) &&
                      p.preDistribution &&
                      p.preDistribution.some((v) => v > 0)
                  );
                  generatePairsFromPlans(activePlans).forEach((cnt, key) =>
                    dataPairs.set(key, (dataPairs.get(key) || 0) + cnt)
                  );
                });

                const nRows = ALL_PAIRS.length;
                const rowH = chartH / nRows;
                const barH = Math.max(3, Math.min(14, rowH * 0.68));
                const labelFS = Math.max(8, Math.round(FS * 0.72));
                const totalResponses = Array.from(dataPairs.values()).reduce(
                  (s, v) => s + v,
                  0
                );
                const perLayer = areaOpacityFor(Math.max(1, totalResponses));
                const barOpacity = (cnt) =>
                  Math.min(0.92, 1 - Math.pow(1 - perLayer, cnt));

                return (
                  <>
                    {RATINGS.map(
                      (r, i) =>
                        i % 2 === 1 && (
                          <rect
                            key={r}
                            x={PAD_LEFT + i * colW}
                            y={PAD_TOP}
                            width={colW}
                            height={chartH}
                            fill="#f9fafb"
                          />
                        )
                    )}
                    {Array.from({ length: 11 }, (_, i) => (
                      <line
                        key={i}
                        x1={PAD_LEFT + i * colW}
                        y1={PAD_TOP}
                        x2={PAD_LEFT + i * colW}
                        y2={PAD_TOP + chartH}
                        stroke="#e5e7eb"
                        strokeWidth={1}
                      />
                    ))}
                    {ALL_PAIRS.map(({ b, a, diagonal }, idx) => {
                      const rowY = PAD_TOP + chartH - (idx + 1) * rowH;
                      const barY = rowY + (rowH - barH) / 2;
                      const key = `${b}·${a}`;
                      const cnt = dataPairs.get(key) || 0;
                      const bX = PAD_LEFT + (b - 1) * colW;
                      const bW = (a - b + 1) * colW;
                      const op = cnt > 0 ? barOpacity(cnt) : 0;
                      return (
                        <g key={key}>
                          {diagonal && (
                            <line
                              x1={PAD_LEFT}
                              y1={rowY + rowH / 2}
                              x2={PAD_LEFT + chartW}
                              y2={rowY + rowH / 2}
                              stroke="#e5e7eb"
                              strokeWidth={0.5}
                              strokeDasharray="2,4"
                            />
                          )}
                          {cnt > 0 && (
                            <>
                              <rect
                                x={bX}
                                y={barY}
                                width={bW}
                                height={barH}
                                fill={`rgb(${BASE_COLOR})`}
                                rx={1}
                                opacity={op}
                                pointerEvents="none"
                              />

                              <rect
                                x={bX}
                                y={rowY}
                                width={bW}
                                height={rowH}
                                fill="transparent"
                                pointerEvents="all"
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={(e) => {
                                  setHoverPair({ b, a, cnt });
                                  setTooltipPos({
                                    x: e.clientX,
                                    y: e.clientY,
                                  });
                                }}
                                onMouseMove={(e) =>
                                  setTooltipPos({
                                    x: e.clientX,
                                    y: e.clientY,
                                  })
                                }
                                onMouseLeave={() => setHoverPair(null)}
                              />
                            </>
                          )}
                          {diagonal && (
                            <text
                              x={PAD_LEFT - 6}
                              y={rowY + rowH / 2}
                              textAnchor="end"
                              dominantBaseline="middle"
                              fontSize={labelFS}
                              fill={cnt > 0 ? LABEL_COLOR : AXIS_COLOR}
                              fontWeight={cnt > 0 ? 600 : 400}
                            >
                              {b}·{a}
                            </text>
                          )}
                        </g>
                      );
                    })}
                    {RATINGS.map((r, i) => (
                      <g key={r}>
                        <line
                          x1={PAD_LEFT + (i + 0.5) * colW}
                          y1={PAD_TOP + chartH}
                          x2={PAD_LEFT + (i + 0.5) * colW}
                          y2={PAD_TOP + chartH + 5}
                          stroke={AXIS_COLOR}
                          strokeWidth={1}
                        />
                        <text
                          x={PAD_LEFT + (i + 0.5) * colW}
                          y={PAD_TOP + chartH + 21}
                          textAnchor="middle"
                          fontSize={FS}
                          fill={LABEL_COLOR}
                        >
                          {r}
                        </text>
                      </g>
                    ))}
                  </>
                );
              })()}

            {/* ── Trend area chart ── */}
            {mode === 'trend' &&
              (() => {
                const areaOpacity = areaOpacityFor(activities.length);
                const postFill = `rgba(${BASE_COLOR},${areaOpacity})`;
                const postStroke = `rgba(${BASE_COLOR},${Math.min(
                  1,
                  areaOpacity * 2
                )})`;
                const preFill = `rgba(${PRE_COLOR},${areaOpacity})`;
                const preStroke = `rgba(${PRE_COLOR},${Math.min(
                  1,
                  areaOpacity * 2
                )})`;

                const hasPreData = ratingAgg.hasPreData;

                return (
                  <>
                    {!hiddenSeries.has('after') &&
                      activities.map((a) => (
                        <path
                          key={`post-${a.id}`}
                          d={buildPath(
                            a.distribution,
                            xScale,
                            yScale,
                            chartH,
                            PAD_TOP
                          )}
                          fill={postFill}
                          stroke={postStroke}
                          strokeWidth={1}
                        />
                      ))}
                    {hasPreData &&
                      !hiddenSeries.has('before') &&
                      activities.map((a) => {
                        if (
                          !a.preDistribution ||
                          !a.preDistribution.some((v) => v > 0)
                        )
                          return null;
                        return (
                          <path
                            key={`pre-${a.id}`}
                            d={buildPath(
                              a.preDistribution,
                              xScale,
                              yScale,
                              chartH,
                              PAD_TOP
                            )}
                            fill={preFill}
                            stroke={preStroke}
                            strokeWidth={1}
                          />
                        );
                      })}
                    {ratingAgg.postAvg != null &&
                      !hiddenSeries.has('avgAfter') && (
                        <AvgLine
                          x={xScale(ratingAgg.postAvg - 1)}
                          top={PAD_TOP}
                          bottom={PAD_TOP + chartH}
                          label={
                            data?.isPrePost
                              ? t('charts.average_after')
                              : t('charts.average')
                          }
                          color={`rgb(${BASE_COLOR})`}
                          fs={FS}
                        />
                      )}
                    {ratingAgg.preAvg != null &&
                      !hiddenSeries.has('avgBefore') && (
                        <AvgLine
                          x={xScale(ratingAgg.preAvg - 1)}
                          top={PAD_TOP}
                          bottom={PAD_TOP + chartH}
                          label={t('charts.average_before')}
                          color={`rgb(${PRE_COLOR})`}
                          fs={FS}
                        />
                      )}
                  </>
                );
              })()}

            {/* X axis ticks — trend only */}
            {mode === 'trend' &&
              RATINGS.map((r, i) => {
                const x = xScale(i);
                return (
                  <g key={r}>
                    <line
                      x1={x}
                      y1={PAD_TOP + chartH}
                      x2={x}
                      y2={PAD_TOP + chartH + 5}
                      stroke={AXIS_COLOR}
                      strokeWidth={1}
                    />
                    <text
                      x={x}
                      y={PAD_TOP + chartH + 21}
                      textAnchor="middle"
                      fontSize={FS}
                      fill={LABEL_COLOR}
                    >
                      {r}
                    </text>
                  </g>
                );
              })}

            {/* Annotation row: "Not at all" ← response rating for [measure name] → "The most" */}
            {(() => {
              const adaptiveMaxChars =
                chartW < 350 ? 20 : chartW < 600 ? 36 : chartW < 800 ? 48 : 60;
              const annotationLabel = t('charts.reponse_rating_for', {
                displayName,
              });
              const lines = splitLabel(annotationLabel, adaptiveMaxChars);
              const midX = PAD_LEFT + chartW / 2;
              const baseY = PAD_TOP + chartH + 46;
              const line2Y = baseY + 16;
              const arrowY = baseY - 5;
              const charW = FS * 0.58;
              const leftHW = ('Not at all'.length * charW) / 2;
              const rightHW = ('The most'.length * charW) / 2;
              const nameHW =
                (Math.max(...lines.map((l) => l.length)) * charW) / 2;
              const leftLineX1 = midX - nameHW - 14;
              const leftLineX2 = PAD_LEFT + leftHW * 2 + 10;
              const rightLineX1 = midX + nameHW + 14;
              const rightLineX2 = PAD_LEFT + chartW - rightHW * 2 - 10;
              const minLineLen = 12;
              const minRoom = Math.min(
                leftLineX1 - leftLineX2,
                rightLineX2 - rightLineX1
              );
              const annotWeight = minRoom >= 22 ? 700 : 400;
              const annotSize = minRoom >= -10 ? FS + 1 : FS - 1;
              return (
                <>
                  <text
                    x={PAD_LEFT}
                    y={baseY}
                    textAnchor="start"
                    fontSize={annotSize}
                    fontWeight={annotWeight}
                    fill={LABEL_COLOR}
                  >
                    {t('charts.not_at_all')}
                  </text>
                  {leftLineX1 - leftLineX2 > minLineLen && (
                    <line
                      x1={leftLineX1}
                      y1={arrowY}
                      x2={leftLineX2}
                      y2={arrowY}
                      stroke={AXIS_COLOR}
                      strokeWidth={1}
                      markerEnd="url(#aor-arrowHead)"
                    />
                  )}
                  <text
                    x={midX}
                    y={baseY}
                    textAnchor="middle"
                    fontSize={annotSize}
                    fontWeight={annotWeight}
                    fill={LABEL_COLOR}
                  >
                    {lines[0]}
                  </text>
                  {lines[1] && (
                    <text
                      x={midX}
                      y={line2Y}
                      textAnchor="middle"
                      fontSize={annotSize}
                      fontWeight={annotWeight}
                      fill={LABEL_COLOR}
                    >
                      {lines[1]}
                    </text>
                  )}
                  {rightLineX2 - rightLineX1 > minLineLen && (
                    <line
                      x1={rightLineX1}
                      y1={arrowY}
                      x2={rightLineX2}
                      y2={arrowY}
                      stroke={AXIS_COLOR}
                      strokeWidth={1}
                      markerEnd="url(#aor-arrowHead)"
                    />
                  )}
                  <text
                    x={PAD_LEFT + chartW}
                    y={baseY}
                    textAnchor="end"
                    fontSize={annotSize}
                    fontWeight={annotWeight}
                    fill={LABEL_COLOR}
                  >
                    {t('charts.the_most')}
                  </text>
                </>
              );
            })()}

            {/* Y axis label — area/bar graph only */}
            {mode === 'trend' &&
              (() => {
                const yLbl = hasThirdParty
                  ? t('charts.participants_in_evaluation')
                  : t('charts.number_of_responses');
                return (
                  <g
                    transform={`translate(${Y_COL_X}, ${PAD_TOP + chartH / 2})`}
                    fill={LABEL_COLOR}
                  >
                    <text
                      transform="rotate(-90)"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={FS + 1}
                      fontWeight={700}
                      letterSpacing="0.06em"
                    >
                      {yLbl}
                    </text>
                  </g>
                );
              })()}

            {mode === 'trend' && (
              <>
                <rect
                  x={PAD_LEFT}
                  y={PAD_TOP}
                  width={chartW}
                  height={chartH}
                  fill="transparent"
                  pointerEvents="all"
                  style={{ cursor: 'crosshair' }}
                  onMouseMove={(e) => {
                    const svgEl = containerRef?.current?.querySelector('svg');
                    if (!svgEl) return;
                    const bbox = svgEl.getBoundingClientRect();
                    if (bbox.width === 0) return;
                    const localX = (e.clientX - bbox.left) * (W / bbox.width);
                    const index = Math.round(
                      ((localX - PAD_LEFT) / chartW) * (RATINGS.length - 1)
                    );
                    const clamped = Math.max(
                      0,
                      Math.min(RATINGS.length - 1, index)
                    );
                    // keep hover only if there is data dont show hovers with 0 0 data:)
                    const hasAfterData =
                      !hiddenSeries.has('after') &&
                      ratingAgg.postAgg[clamped] > 0;
                    const hasBeforeData =
                      ratingAgg.hasPreData &&
                      !hiddenSeries.has('before') &&
                      ratingAgg.preAgg[clamped] > 0;
                    if (!hasAfterData && !hasBeforeData) {
                      setHoverRating(null);
                      setTooltipPos(null);
                      return;
                    }
                    setHoverRating(RATINGS[clamped]);
                    setTooltipPos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseLeave={() => {
                    setHoverRating(null);
                    setTooltipPos(null);
                  }}
                />
                {!isExport && hoverRating != null && (
                  <line
                    className="or__hover-crosshair"
                    x1={xScale(hoverRating - 1)}
                    y1={PAD_TOP}
                    x2={xScale(hoverRating - 1)}
                    y2={PAD_TOP + chartH}
                    stroke={AXIS_COLOR}
                    strokeWidth={1}
                    strokeDasharray="3,3"
                    pointerEvents="none"
                  />
                )}
              </>
            )}
          </svg>
        </div>

        {/* Hover tooltip for area charts */}
        {!isExport &&
          mode === 'trend' &&
          hoverRating != null &&
          tooltipPos &&
          createPortal(
            <div
              className="or__hover-tooltip"
              style={{ left: tooltipPos.x, top: tooltipPos.y }}
            >
              <div className="or__hover-tooltip-title">
                {t('charts.rating')} {hoverRating}
              </div>
              {!hiddenSeries.has('after') && (
                <div>
                  <Trans
                    i18nKey="charts.post_data_fmt"
                    values={{
                      value: ratingAgg.postAgg[hoverRating - 1],
                      afterOrResponsesLabel: data?.isPrePost
                        ? 'after'
                        : 'responses',
                    }}
                  />
                </div>
              )}
              {ratingAgg.hasPreData && !hiddenSeries.has('before') && (
                <div>
                  <Trans
                    i18nKey="charts.pre_data_fmt"
                    values={{ value: ratingAgg.preAgg[hoverRating - 1] }}
                  />
                </div>
              )}
              {(ratingAgg.postAvg != null || ratingAgg.preAvg != null) && (
                <div className="or__hover-tooltip-divider" />
              )}
              {ratingAgg.postAvg != null && !hiddenSeries.has('avgAfter') && (
                <div>
                  <Trans
                    i18nKey="charts.average_after_fmt"
                    values={{
                      label: data?.isPrePost
                        ? t('charts.average_after')
                        : t('charts.average'),
                      avg: Math.round(ratingAgg.postAvg * 10) / 10,
                    }}
                  />
                </div>
              )}
              {ratingAgg.preAvg != null && !hiddenSeries.has('avgBefore') && (
                <div>
                  <Trans
                    i18nKey="charts.average_before_fmt"
                    values={{ avg: Math.round(ratingAgg.preAvg * 10) / 10 }}
                  />
                </div>
              )}
            </div>,
            document.body
          )}

        {/* Hover tooltip for individual-trend bars, also portals*/}
        {!isExport &&
          mode === 'individual' &&
          hoverPair &&
          tooltipPos &&
          createPortal(
            <div
              className="or__hover-tooltip"
              style={{ left: tooltipPos.x, top: tooltipPos.y }}
            >
              <div className="or__hover-tooltip-title">
                {t('charts.before_and_after_counts', {
                  b: hoverPair.b,
                  a: hoverPair.a,
                })}
              </div>
              <div>
                <Trans
                  i18nKey="charts.num_participants_html"
                  values={{
                    cnt: hoverPair.cnt,
                    value: t('charts.participants_plural', {
                      count: hoverPair.cnt,
                    }),
                  }}
                />
              </div>
            </div>,
            document.body
          )}
      </>
    );
  };

  if (loading && !data) {
    return <div className="or__state">{t('charts.loading_ratings')}…</div>;
  }

  if (error) {
    return <div className="or__state or__state--error">{error}</div>;
  }

  if (!data || (data.activities || []).length === 0) {
    if (hideNoDataMessage) return null;
    return (
      <div className="chart-activity-outcome-ratings">
        <div className="method-heading-box">
          {t('activity.evaluation_plan.no_responses_recorded')}
        </div>
      </div>
    );
  }

  const modes = [
    ...new Set(
      (visibleModes
        ? visibleModes.filter(
            (m) => m === 'trend' || (m === 'individual' && data?.isPrePost)
          )
        : [chartMode]
      ).filter((m) => m !== 'individual' || !allManagerAssessed)
    ),
  ];

  // Rather than show the header/stats/filter/prose around an empty chart, render nothing.
  if (visibleModes && modes.length === 0) {
    return null;
  }

  const splitView = modes.length > 1;
  // Only the Individual graph is on screen (not stacked alongside Trend) —
  // the method filter's manager-assessed checkboxes are inert here, since
  // Individual never includes them regardless of their checked state.
  const showingIndividualOnly = !splitView && modes[0] === 'individual';

  // Whatever's actually selectable once unavailable modes are filtered out. A radio
  // group with one option toggles nothing, so it's not worth showing.
  const availableModeOptions = [
    { mode: 'trend', label: t('charts.modes.trend_rate_of_change') },
    { mode: 'individual', label: t('charts.modes.individual_rate_of_change') },
  ].filter(({ mode }) => mode !== 'individual' || !allManagerAssessed);

  const filterNote = reportMode && isFiltered && (
    <div className="or__filter-note">
      {t('charts.this_chart_has_been_filtered_by', {
        filterKindLabel,
        evalPlanNames: includedPlanNames.join(', '),
      })}
    </div>
  );

  return (
    <div className="chart-activity-outcome-ratings" ref={setMeasureEl}>
      {/* Options row — Include in report */}
      {showIncludeToggle && (
        <div className="or__options">
          <div className="aor__stats-include">
            <span className="or__include-label">
              {t('activity.report.show_in_report')}
            </span>
            <div className="or__include-btns">
              {['Yes', 'No'].map((btn, i) => {
                const active = btn === 'Yes' ? included : !included;
                return (
                  <button
                    key={btn}
                    disabled={capturing}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={async () => {
                      const wantInclude = btn === 'Yes';
                      if (wantInclude && !included) {
                        setCapturing(true);
                        try {
                          const png = await captureForExport('trend');
                          setIncluded(true);
                          if (onIncluded) onIncluded(png);
                        } catch (e) {
                          console.error('Chart PNG capture failed', e);
                        } finally {
                          setCapturing(false);
                        }
                      } else if (!wantInclude && included) {
                        setIncluded(false);
                        if (onExcluded) onExcluded();
                      }
                    }}
                    className={`or__include-btn${
                      i === 0 ? ' or__include-btn--sep' : ''
                    }${active ? ' or__include-btn--active' : ''}`}
                  >
                    {capturing && btn === 'Yes'
                      ? '…'
                      : btn === 'Yes'
                        ? t('actions.yes')
                        : t('actions.no')}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Chart mode tabs — pre/post outcomes only, and only when there's
          more than one mode to actually pick between e.g. Individual drops out entirely once
          nothing is pairable, per allManagerAssessed. More chart types will be
          added here over time, so this is labelled as a category selector
          rather than a plain pair of tabs... bar chart option to be added soon:)  */}
      {data?.isPrePost && !visibleModes && availableModeOptions.length > 1 && (
        <div className="or__chart-tabs-wrap">
          <div className="or__chart-radios">
            {availableModeOptions.map(({ mode, label }) => (
              <label key={mode} className="or__chart-radio">
                <input
                  type="radio"
                  name={chartModeName}
                  checked={chartMode === mode}
                  onChange={() => setChartMode(mode)}
                  style={{
                    cursor: 'pointer',
                    accentColor: `rgb(${BASE_COLOR})`,
                  }}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}

      {activities.length === 0 ? (
        <div className="or__state">
          {reportMode
            ? t('charts.no_data_for_filter')
            : t('charts.no_data_for_current_eval_plan_choose_one_above')}
        </div>
      ) : (
        <>
          {/* Split view titles each chart individually instead (below), so
              either graph reads standalone in the report. */}
          {!splitView && <h3 className="or__title">{displayName}</h3>}

          {!splitView && filterNote}

          {/* In split view + reportMode the row would be empty (filter and
              download both need !reportMode, stats move per chart). */}
          {!(splitView && reportMode) && (
            <div
              className="or__stats-row"
              style={{ maxWidth: screenLayout.displayW }}
            >
              {/* Individual mode's stats differ from Trend's (third-party
                methods never count there) — when both show at once there's
                no single correct shared figure, so each gets its own
                stats line above its chart instead. */}
              {!splitView ? (
                <div className="or__stats">
                  <StatsLine
                    {...getStatsForMode(modes[0])}
                    showPlans={!(evaluationPlanIds?.length === 1)}
                    multiseries={isMultiseriesView}
                    hasManagerAssessed={
                      modes[0] === 'trend' && hasManagerAssessed
                    }
                  />
                </div>
              ) : (
                <div />
              )}
              <div className="or__svg-toolbar">
                {!reportMode &&
                  allPlans.length > 1 &&
                  (() => {
                    const activeTab = activeFilterTab;
                    const badgeCount =
                      activeTab === 'method'
                        ? selectedMethods.size
                        : activeTab === 'multiseries'
                          ? seriesGroups.filter((g) =>
                              g.plans.every((p) => selectedPlanIds.has(p.id))
                            ).length
                          : selectedPlanIds.size;
                    const badgeTotal =
                      activeTab === 'method'
                        ? allMethods.length
                        : activeTab === 'multiseries'
                          ? seriesGroups.length
                          : allPlans.length;
                    return (
                      <Popup
                        on="click"
                        position="bottom right"
                        trigger={
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            className="or__filter-btn"
                            title={t('charts.filter_by_method_or_eval_plan')}
                          >
                            <Icon name="sliders horizontal" />
                            {isFiltered && (
                              <span className="or__filter-badge">
                                {badgeCount}/{badgeTotal}
                              </span>
                            )}
                          </button>
                        }
                      >
                        <div className="or__filter-panel">
                          {tabsAvailable.length > 1 && (
                            <div className="or__filter-tabs">
                              {showMethodTab && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFilterTab('method');
                                    resetPlansToAll();
                                  }}
                                  className={`or__filter-tab${
                                    activeTab === 'method'
                                      ? ' or__filter-tab--active'
                                      : ''
                                  }`}
                                >
                                  {t('charts.method')}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setFilterTab('plan');
                                  resetMethodsToAll();
                                }}
                                className={`or__filter-tab${
                                  activeTab === 'plan'
                                    ? ' or__filter-tab--active'
                                    : ''
                                }`}
                              >
                                {t('activity.evaluation.evaluation_plan')}
                              </button>
                              {hasMultiseries && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFilterTab('multiseries');
                                    resetMethodsToAll();
                                    resetSeriesToAll();
                                  }}
                                  className={`or__filter-tab${
                                    activeTab === 'multiseries'
                                      ? ' or__filter-tab--active'
                                      : ''
                                  }`}
                                >
                                  {t('charts.multiseries')}
                                </button>
                              )}
                            </div>
                          )}
                          {activeTab === 'method' ? (
                            <FilterCheckList
                              label={t('charts.filter_by_method')}
                              onSelectAll={selectAllMethods}
                              items={allMethods.map((m) => {
                                // Individual never includes manager-assessed
                                // methods regardless of this checkbox — while only the
                                // Individual graph is showing, grey it out which helps show its not usable
                                const isUnavailable =
                                  showingIndividualOnly &&
                                  NO_INDIVIDUAL_PAIRING_RE.test(m);
                                return {
                                  key: m,
                                  checked:
                                    !isUnavailable && selectedMethods.has(m),
                                  isUnavailable,
                                  onChange: () => toggleMethod(m),
                                  displayLabel: titleCaseMethod(m),
                                };
                              })}
                            />
                          ) : activeTab === 'multiseries' ? (
                            <FilterCheckList
                              label={t('charts.filter_by_series')}
                              onSelectAll={selectAllSeries}
                              items={seriesGroups.map((g) => {
                                const planIds = g.plans.map((p) => p.id);
                                const planMethod = planMethodById.get(
                                  g.plans[0]?.id
                                );
                                const isUnavailable =
                                  showingIndividualOnly &&
                                  NO_INDIVIDUAL_PAIRING_RE.test(
                                    planMethod || ''
                                  );
                                return {
                                  key: g.key,
                                  checked:
                                    !isUnavailable &&
                                    planIds.every((id) =>
                                      selectedPlanIds.has(id)
                                    ),
                                  isUnavailable,
                                  onChange: () => toggleSeriesGroup(planIds),
                                  displayLabel: (
                                    <>
                                      {g.label}
                                      <span className="or__series-stage-count">
                                        {' '}
                                        (
                                        {t('charts.num_stages_aggregated', {
                                          num: g.plans.length,
                                        })}
                                        )
                                      </span>
                                    </>
                                  ),
                                };
                              })}
                            />
                          ) : (
                            <FilterCheckList
                              label={t(
                                'charts.evaluation_plans.filter_by_evaluation_plan'
                              )}
                              onSelectAll={selectAllPlans}
                              items={allPlans.map((p) => {
                                const planMethod = planMethodById.get(p.id);
                                const isUnavailable =
                                  showingIndividualOnly &&
                                  NO_INDIVIDUAL_PAIRING_RE.test(
                                    planMethod || ''
                                  );
                                return {
                                  key: p.id,
                                  checked:
                                    !isUnavailable && selectedPlanIds.has(p.id),
                                  isUnavailable,
                                  onChange: () => togglePlan(p.id),
                                  displayLabel:
                                    p.name ||
                                    `${t('charts.evaluation_plans.plan')} ${p.id}`,
                                };
                              })}
                            />
                          )}
                        </div>
                      </Popup>
                    );
                  })()}
                {!reportMode && !splitView && (
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() =>
                      handleSavePng(
                        modes[0],
                        getStatsForMode(modes[0]),
                        isMultiseriesView
                      )
                    }
                    disabled={savingPng}
                    className="or__save-png-btn or__save-png-btn--corner"
                    title={t('charts.download_graph')}
                    aria-label={t('charts.download_graph')}
                  >
                    <span className="or__save-png-label">
                      {savingPng
                        ? `${t('common.downloading')}…`
                        : t('charts.download_graph')}
                    </span>
                    <Icon name="download" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Static PNG — shown when chart has been captured and included in report */}
          {capturedPng && included && (
            <img
              src={capturedPng}
              alt="Outcome ratings chart"
              className="or__captured-png"
              style={{ maxWidth: screenLayout.displayW }}
            />
          )}

          {modes.map((mode, i) => (
            <Fragment key={mode}>
              {splitView && (
                <h3 className="or__title or__chart-section-title">
                  {displayName}
                </h3>
              )}
              {splitView && i === 0 && filterNote}
              {splitView && (
                <div
                  className="or__stats-row"
                  style={{ maxWidth: screenLayout.displayW }}
                >
                  <div className="or__stats">
                    <StatsLine
                      {...getStatsForMode(mode)}
                      showPlans={!(evaluationPlanIds?.length === 1)}
                      multiseries={isMultiseriesView}
                      hasManagerAssessed={
                        mode === 'trend' && hasManagerAssessed
                      }
                    />
                  </div>
                  {!reportMode && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() =>
                        handleSavePng(
                          mode,
                          getStatsForMode(mode),
                          isMultiseriesView,
                          mode === 'trend' && hasManagerAssessed
                        )
                      }
                      disabled={savingPng}
                      className="or__save-png-btn or__save-png-btn--sm"
                      title={t('charts.download_graph')}
                      aria-label={t('charts.download_graph')}
                    >
                      <span className="or__save-png-label">
                        {savingPng
                          ? `{t('common.downloading')}…`
                          : t('charts.download_graph')}
                      </span>
                      <Icon name="download" />
                    </button>
                  )}
                </div>
              )}
              {renderChartBlock(
                mode,
                mode === 'trend' ? svgRef : svgRefIndividual,
                screenLayout
              )}
            </Fragment>
          ))}

          {exportMode && (
            <div
              ref={exportWrapRef}
              className="or__export-render"
              aria-hidden="true"
            >
              {renderChartBlock(exportMode, null, exportLayout, true)}
            </div>
          )}

          {/* Prose summary */}
          {showProse && prose && prose.length > 0 && (
            <div className="or__prose">
              <ul className="or__prose-list">
                {prose.map((p, i) => (
                  <li key={i} className="or__prose-text">
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const ActivityOutcomeRatings = React.forwardRef(
  ActivityOutcomeRatingsInner
);
export default ActivityOutcomeRatings;
